import { Capacitor } from '@capacitor/core';
import { Loader2 } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../../../lib/supabase';
import { markOnboardingCompleteForSignin } from '../../../modules/onboarding/markOnboardingCompleteForSignin';
import { startAuthTransition } from '../../../app/router/postAuthLanding';
import { logEvent } from '../../../services/loggingService';
import { attributeSignupIfPending } from '../../../services/referralAttribution';
import { attributeOutreachSignupIfPending } from '../../../services/outreachAttribution';

const IS_NATIVE = Capacitor.isNativePlatform();

function getNativeOAuthRedirectUrl(): string {
  return IS_NATIVE
    ? 'com.gograbjob.app://auth/callback'
    : `${window.location.origin}/auth/confirm`;
}

// ──────────────────────────────────────────────────────────────────────────────
// Google Identity Services type declarations
// ──────────────────────────────────────────────────────────────────────────────
interface GisCredentialResponse {
  credential: string;
  select_by: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: GisCredentialResponse) => void;
            nonce?: string;
            /** Opt in to FedCM; reduces legacy One Tap deprecation noise in DevTools. */
            use_fedcm_for_prompt?: boolean;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          prompt: (
            momentListener?: (notification: {
              isNotDisplayed: () => boolean;
              isSkippedMoment: () => boolean;
              getDismissedReason?: () => string;
            }) => void
          ) => void;
          cancel: () => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Brand icons
// ──────────────────────────────────────────────────────────────────────────────
const GoogleIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const LinkedInIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="#0A66C2">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

function loadGisScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) { resolve(); return; }
    if (document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
      const t = setInterval(() => { if (window.google?.accounts?.id) { clearInterval(t); resolve(); } }, 100);
      setTimeout(() => { clearInterval(t); resolve(); }, 6000);
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true; s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────────
interface OAuthLoginFormProps {
  authMode: 'signin' | 'signup';
  onSuccess?: () => void;
  onError?: (error: string) => void;
  /** Fires as soon as Google/LinkedIn credential exchange begins (before navigation). */
  onAuthStarted?: () => void;
  disabled?: boolean;
  /** wrangle = split login (Google only); onboarding = PageRampShell card OAuth */
  variant?: 'default' | 'pin-light' | 'pin-centered' | 'untitled-split' | 'readymade' | 'wrangle' | 'onboarding';
  oauthProviders?: Array<'google' | 'linkedin'>;
}

const OAuthLoginForm: React.FC<OAuthLoginFormProps> = ({
  authMode,
  onSuccess,
  onError,
  onAuthStarted,
  disabled = false,
  variant = 'default',
  oauthProviders = ['google', 'linkedin'],
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<'google' | 'linkedin' | null>(null);
  const [hoveredBtn, setHoveredBtn] = useState<'google' | 'linkedin' | null>(null);

  // When One Tap is unavailable (user not signed into Google in their browser),
  // we switch to showing the GIS rendered button which always works.
  const [showGisButton, setShowGisButton] = useState(false);
  const gisButtonRef = useRef<HTMLDivElement>(null);
  const googleBtnContainerRef = useRef<HTMLDivElement>(null);

  // Pre-load GIS so first click is instant
  useEffect(() => {
    if (GOOGLE_CLIENT_ID) loadGisScript().catch(() => {});
  }, []);

  // Render the GIS button once the container div is visible
  useEffect(() => {
    if (!showGisButton || !gisButtonRef.current || !window.google?.accounts?.id) return;
    gisButtonRef.current.innerHTML = '';
    const width = googleBtnContainerRef.current?.offsetWidth || 320;
    window.google.accounts.id.renderButton(gisButtonRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: authMode === 'signup' ? 'signup_with' : 'signin_with',
      shape: 'rectangular',
      width,
    });
  }, [showGisButton, authMode]);

  const persistPricingPlanIfPresent = () => {
    const plan = new URLSearchParams(window.location.search).get('plan');
    const billing = new URLSearchParams(window.location.search).get('billing');
    if (plan && billing) {
      sessionStorage.setItem('pricing_plan_after_auth', JSON.stringify({ planId: plan, billing }));
    }
  };

  // ── Google Sign-In ──────────────────────────────────────────────────────────
  //
  // Strategy (web):
  //   - Do not call google.accounts.id.prompt() — it triggers FedCM/GSI deprecation
  //     console warnings (display/skipped "moment" APIs).
  //   - initialize() with use_fedcm_for_prompt, then render the official GIS button.
  //   - Exchange the ID token via signInWithIdToken (through app proxy on prod).
  //
  // Nonce flow (required to prevent "Passed nonce and nonce in id_token should
  // either both exist or not" error from Supabase GoTrue):
  //   - Generate a random raw nonce.
  //   - SHA-256 hash it → pass the hex digest to GIS `initialize({ nonce })` so
  //     GIS embeds it in the id_token's `nonce` claim.
  //   - Pass the raw nonce to `signInWithIdToken({ nonce })` so GoTrue can verify.
  //
  const generateNonce = async (): Promise<{ raw: string; hashed: string }> => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const raw = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
    const hashBuf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
    const hashed = Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, '0')).join('');
    return { raw, hashed };
  };

  const handleGoogleAuth = async () => {
    if (!GOOGLE_CLIENT_ID) {
      onError?.(t('auth.googleNotConfigured', 'Google sign-in is not configured.'));
      return;
    }

    try {
      setLoading('google');
      setShowGisButton(false);
      localStorage.setItem('oauth_intended_action', authMode);
      persistPricingPlanIfPresent();

      // On Android/iOS native WebView, GIS (accounts.google.com/gsi/client) cannot
      // load because it requires a real browser context. Use Supabase OAuth redirect
      // with a custom deep-link scheme instead.
      if (IS_NATIVE) {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: getNativeOAuthRedirectUrl(),
            queryParams: { access_type: 'offline', prompt: 'consent' },
          },
        });
        if (error) throw error;
        await logEvent({ level: 'info', action: `google_oauth_native_${authMode}_initiated`, details: { authMode } });
        return;
      }

      window.google?.accounts?.id?.cancel();

      // Web: Google Identity Services — official button only (no One Tap prompt)
      await loadGisScript();
      if (!window.google?.accounts?.id) throw new Error(t('auth.googleGisLoadFailed', 'Google Identity Services could not be loaded.'));

      const { raw: rawNonce, hashed: hashedNonce } = await generateNonce();

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID!,
        nonce: hashedNonce,
        use_fedcm_for_prompt: true,
        auto_select: false,
        cancel_on_tap_outside: true,
        callback: async (response: GisCredentialResponse) => {
          setShowGisButton(false);
          if (!response.credential) {
            onError?.(t('auth.googleNoCredential', 'Google sign-in returned no credential.'));
            return;
          }
          onAuthStarted?.();
          startAuthTransition();
          try {
            const { data, error } = await supabase.auth.signInWithIdToken({
              provider: 'google',
              token: response.credential,
              nonce: rawNonce,
            });
            if (error) throw error;
            if (!data.session) throw new Error(t('auth.noSessionReturned', 'No session returned from Supabase.'));
            sessionStorage.setItem('tab_session_active', Date.now().toString());
            if (authMode === 'signin') {
              void markOnboardingCompleteForSignin(data.session.user.id);
            }
            if (authMode === 'signup') {
              try { await attributeSignupIfPending(); } catch { /* best-effort */ }
              try { await attributeOutreachSignupIfPending(); } catch { /* best-effort */ }
            }
            onSuccess?.();
            void logEvent({ level: 'info', action: `google_gis_${authMode}_success`, details: { authMode } });
          } catch (err) {
            console.error('Google credential exchange failed:', err);
            onError?.(err instanceof Error ? err.message : t('auth.googleAuthFailed', 'Google authentication failed'));
          }
        },
      });

      setShowGisButton(true);
    } catch (error) {
      console.error('Google auth error:', error);
      if (!IS_NATIVE) window.google?.accounts?.id?.cancel();
      onError?.(error instanceof Error ? error.message : t('auth.googleAuthFailed', 'Google authentication failed'));
    } finally {
      setLoading(null);
    }
  };

  // ── LinkedIn OAuth (standard redirect) ──────────────────────────────────────
  const handleLinkedInAuth = async () => {
    try {
      setLoading('linkedin');
      localStorage.setItem('oauth_intended_action', authMode);
      persistPricingPlanIfPresent();

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: getNativeOAuthRedirectUrl(),
          skipBrowserRedirect: false,
          scopes: 'openid profile email',
        },
      });
      if (error) throw error;

      await logEvent({ level: 'info', action: `linkedin_oauth_${authMode}_initiated`, details: { authMode } });
    } catch (error) {
      console.error('LinkedIn OAuth error:', error);
      onError?.(error instanceof Error ? error.message : t('auth.linkedInAuthFailed', 'LinkedIn authentication failed'));
      setLoading(null);
    }
  };

  const btnStyle = (btn: 'google' | 'linkedin'): React.CSSProperties => {
    if (variant === 'pin-light' || variant === 'pin-centered' || variant === 'untitled-split' || variant === 'readymade' || variant === 'wrangle' || variant === 'onboarding') {
      return {};
    }
    return {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '12px 16px',
    /* Theme-adaptive: white card in light, dark card in dark */
    background: hoveredBtn === btn
      ? 'var(--vscode-bg-hover, var(--vscode-bg-elevated))'
      : 'var(--vscode-bg-elevated)',
    color: 'var(--vscode-text-primary)',
    border: `1.5px solid ${hoveredBtn === btn ? 'rgba(108,71,255,0.40)' : 'var(--vscode-border-primary)'}`,
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    cursor: disabled || loading !== null ? 'not-allowed' : 'pointer',
    opacity: disabled || loading !== null ? 0.5 : 1,
    transition: 'background 0.15s, border-color 0.15s',
    WebkitTapHighlightColor: 'transparent',
    outline: 'none',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    };
  };

  const authRampOAuthClass =
    'w-full flex items-center justify-center gap-2.5 py-2.5 px-4 text-sm rounded-lg font-medium touch-manipulation border auth-ramp-oauth-btn focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed';
  const wrangleOAuthClass =
    'w-full flex items-center justify-center gap-2.5 py-2.5 px-4 text-sm rounded-lg font-medium text-gray-800 border border-gray-200 bg-white hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed';
  const readymadeOAuthClass =
    'w-full flex items-center justify-center gap-2.5 py-2 px-3.5 text-sm rounded-md font-semibold text-slate-900 border border-slate-300 bg-white hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed';
  const googleUntitledClass =
    'w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 border border-transparent text-gray-700 font-medium py-2.5 px-4 rounded-lg shadow-sm transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed';
  const linkedInUntitledClass =
    'w-full flex items-center justify-center gap-3 bg-[#11131e] hover:bg-[#161927] border border-gray-800 text-gray-200 font-medium py-2.5 px-4 rounded-lg shadow-sm transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed';
  const pinButtonClass =
    'w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 border border-gray-200/80 text-gray-700 font-medium py-2.5 px-4 rounded-xl shadow-sm transition-all duration-200 ease-in-out text-sm disabled:opacity-50 disabled:cursor-not-allowed';
  const btnClassName =
    variant === 'onboarding'
      ? authRampOAuthClass
      : variant === 'wrangle'
        ? wrangleOAuthClass
        : variant === 'readymade'
          ? readymadeOAuthClass
          : variant === 'untitled-split'
            ? undefined
            : variant === 'pin-centered'
              ? pinButtonClass
              : variant === 'pin-light'
                ? 'auth-oauth-btn'
                : undefined;
  const stackClassName = variant === 'pin-light' ? 'auth-oauth-stack' : undefined;
  const stackTailwind =
    variant === 'pin-centered' || variant === 'untitled-split' || variant === 'readymade' || variant === 'wrangle' || variant === 'onboarding'
      ? 'flex flex-col gap-3'
      : undefined;
  const stackStyle =
    variant === 'pin-light' || variant === 'pin-centered' || variant === 'untitled-split' || variant === 'readymade' || variant === 'wrangle' || variant === 'onboarding'
      ? undefined
      : { display: 'flex', flexDirection: 'column' as const, gap: 10 };

  const googleLabel =
    variant === 'wrangle' || (variant === 'onboarding' && authMode === 'signin')
      ? t('auth.continueWithGoogle')
      : authMode === 'signup'
        ? t('auth.signUpWithGoogle')
        : t('auth.signInWithGoogle');

  const showGoogle = oauthProviders.includes('google');
  const showLinkedIn = oauthProviders.includes('linkedin');

  return (
    <div className={stackTailwind ?? stackClassName} style={stackStyle}>

      {/* ── Google button area ─────────────────────────────────────────────── */}
      {showGoogle ? (
      <div ref={googleBtnContainerRef}>
        {!showGisButton ? (
          <button
            onClick={handleGoogleAuth}
            disabled={disabled || loading !== null}
            onMouseEnter={() => variant === 'default' && setHoveredBtn('google')}
            onMouseLeave={() => variant === 'default' && setHoveredBtn(null)}
            className={
              variant === 'untitled-split'
                ? googleUntitledClass
                : variant === 'readymade'
                  ? readymadeOAuthClass
                  : variant === 'wrangle'
                    ? wrangleOAuthClass
                    : variant === 'onboarding'
                      ? authRampOAuthClass
                      : btnClassName
            }
            style={variant === 'default' ? btnStyle('google') : undefined}
          >
            {loading === 'google' ? (
              <><Loader2 className="w-4 h-4 animate-spin" /><span>{t('auth.connecting')}</span></>
            ) : (
              <><GoogleIcon className="w-4 h-4 flex-shrink-0" /><span>{googleLabel}</span></>
            )}
          </button>
        ) : (
          <div className="w-full overflow-hidden rounded-lg [&>div]:!w-full [&_iframe]:!w-full">
            <div ref={gisButtonRef} className="flex w-full justify-center" />
          </div>
        )}
      </div>
      ) : null}

      {/* ── LinkedIn button ────────────────────────────────────────────────── */}
      {showLinkedIn ? (
      <button
        onClick={handleLinkedInAuth}
        disabled={disabled || loading !== null}
        onMouseEnter={() => variant === 'default' && setHoveredBtn('linkedin')}
        onMouseLeave={() => variant === 'default' && setHoveredBtn(null)}
        className={
          variant === 'untitled-split'
            ? linkedInUntitledClass
            : variant === 'readymade'
              ? readymadeOAuthClass
              : btnClassName
        }
        style={variant === 'default' ? btnStyle('linkedin') : undefined}
      >
        {loading === 'linkedin' ? (
          <><Loader2 className="w-4 h-4 animate-spin" /><span>{t('auth.connecting')}</span></>
        ) : (
          <><LinkedInIcon className="w-4 h-4" /><span>{authMode === 'signup' ? t('auth.signUpWithLinkedIn') : t('auth.signInWithLinkedIn')}</span></>
        )}
      </button>
      ) : null}

    </div>
  );
};

export default React.memo(OAuthLoginForm);
